/**
 * String utility helpers — Swedish-safe slugify, truncate, initials.
 */

/** Swedish character replacements for URL-safe slugs. */
const SV_CHAR_MAP: Record<string, string> = {
  å: 'a',
  ä: 'a',
  ö: 'o',
  Å: 'a',
  Ä: 'a',
  Ö: 'o',
  é: 'e',
  è: 'e',
  ü: 'u',
}

/**
 * Create a URL-safe slug from a Swedish string.
 *
 * @example
 * slugify('Åsa Öst') // → 'asa-ost'
 * slugify('Städhjälp & Hemstäd') // → 'stadhjalp-hemstad'
 */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[åäöéèüÅÄÖ]/g, (ch) => SV_CHAR_MAP[ch] ?? ch)
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

/**
 * Truncate text to a maximum length with ellipsis.
 *
 * @example
 * truncate('Hello World', 8) // → 'Hello…'
 */
export function truncate(text: string, maxLength: number, suffix = '…'): string {
  if (text.length <= maxLength) return text
  const trimTo = maxLength - suffix.length
  if (trimTo <= 0) return suffix.slice(0, maxLength)
  // Break at last space to avoid mid-word cuts
  const trimmed = text.slice(0, trimTo)
  const lastSpace = trimmed.lastIndexOf(' ')
  return (lastSpace > 0 ? trimmed.slice(0, lastSpace) : trimmed) + suffix
}

/**
 * Extract initials from a name (max 2 characters).
 *
 * @example
 * initialsFromName('Anna Svensson') // → 'AS'
 * initialsFromName('Kalle') // → 'K'
 */
export function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

/**
 * Capitalize the first letter of a string.
 *
 * @example
 * capitalize('hej') // → 'Hej'
 */
export function capitalize(str: string): string {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}
